import get from "lodash-es/get";
import {
  CERTIFICATE,
  ICONS,
  LOG_FORWARDING,
  APP_CUSTOMIZATION,
  DEFAULT_CUSTOMIZATION,
  NEO_XMLS,
} from "constants/FileNames";
import { WEB, LOG_FWD } from "constants/certificateTypes";
import {
  _getEmailTemplatesInfo,
  _getEventsInfo,
  _getIQConfigInfo,
  _getIQConfigSchemaInfo,
  _getIQPatchTags,
} from "./commonMappers.map";
import { DARK, LIGHT } from "constants/appModes";

export const getAccountFilesMapper = (data) => {
  const {
    email_templates = [],
    certificates = [],
    icon = null,
    app_customizations = [],
    default_customizations = [],
    neo_xml = [],
    events = [],
    iq_config = [],
    iq_patch_tags = {},
    iq_config_schema = {},
  } = data.ipmp_account;
  return [
    _getCertificateInfo(certificates, WEB, CERTIFICATE),
    _getCertificateInfo(certificates, LOG_FWD, LOG_FORWARDING),
    _getNeoXmlInfo(neo_xml),
    _getIQConfigInfo(iq_config),
    _getIQPatchTags(iq_patch_tags),
    _getIQConfigSchemaInfo(iq_config_schema),
    _getIconsInfo(icon),
    _getEmailTemplatesInfo(email_templates),
    _getEventsInfo(events, "id_events_file"),
    _getAppCustomizationInfo(app_customizations),
    _getDefaultCustomizationInfo(default_customizations),
  ];
};

const _getCertificateInfo = (data, certType, name) => {
  const cert = data.find((cert) => cert.type === certType) || {};
  const id_cert = get(cert, "id_cert");
  const meta = get(cert, "meta", []);
  const hasProblem = get(cert, "expired", false);
  return {
    name: name,
    hasFile: id_cert !== undefined,
    hasProblem,
    info: {
      id_cert: id_cert && Number(id_cert),
      pem_filename: get(cert, "cert"),
      key_filename: get(cert, "key"),
      ca_filename: get(cert, "ca_cert"),
      meta,
    },
  };
};

const _getIconsInfo = (data) => {
  const hasFiles = Boolean(data && !!data.filename);
  return {
    name: ICONS,
    hasFile: hasFiles,
    info: hasFiles ? [_parseIconInfo(data)] : [],
  };
};

const _parseIconInfo = (iconInfo) => ({
  filename: get(iconInfo, "filename", "No name"),
  description: get(iconInfo, "description", ""),
});

const _getAppCustomizationInfo = (data) => {
  const idFile = get(data, "id_file");

  return {
    name: APP_CUSTOMIZATION,
    hasFile: !!idFile,
    info: {
      idFile,
    },
  };
};

export const getWebCertificateLinkMapper = (data, certType) => {
  const certificates = data?.ipmp_account?.certificates ?? [];
  const webCert = certificates.find((c) => c.type === certType);
  return webCert.download_url ?? null;
};

export const getAccountAppContentMapper = (data) => {
  const appCustomizations = get(data, "ipmp_account.app_customizations", {});
  const files = get(appCustomizations, "files", []);
  const customizations = get(appCustomizations, "customizations", []);

  const info = {
    files,
    customizations: customizations.map((customization) => {
      const themes = get(customization, "themes", []);
      return {
        [LIGHT]: _getThemeInfo(LIGHT, themes),
        [DARK]: _getThemeInfo(DARK, themes),
        customizationName: get(customization, "name", "Default customization"),
      };
    }),
  };

  return {
    name: APP_CUSTOMIZATION,
    hasFile: !!files?.length,
    info,
  };
};

const _getThemeInfo = (color, themes) => {
  const theme = themes.find((t) => t.brightness === color);
  if (!theme) return {};
  try {
    return {
      name: theme.name,
      ...theme.colors,
      logo: theme.logo || theme.logo_filename || null,
      logoType: theme.logo_type || null,
    };
  } catch {
    return {};
  }
};

const _getDefaultCustomizationInfo = (customizations) => {
  const customizationId = get(customizations, "id_file");
  const info = {
    idFile: customizationId,
    name: get(customizations, "filename"),
    description: get(customizations, "description"),
  };
  return {
    name: DEFAULT_CUSTOMIZATION,
    hasFile: !!customizationId,
    info: info,
  };
};

const _getNeoXmlInfo = (data) => ({
  name: NEO_XMLS,
  hasFile: !!data.length,
  info: data.map((xmlInfo) => ({
    name: xmlInfo.xml_file,
    id: Number(xmlInfo.id_xml_file),
  })),
});

export const getAccountIconContentMapper = (data) =>
  get(data, "ipmp_account.icon.content", "");
